// Phase SIKA-1 — Dynamic Odds Target helper (UI source of truth).
//
// Edge-fn mirror lives in supabase/functions/_shared/sika-odds-target.ts.
// Both files MUST stay in sync. Tests in src/sika/__tests__/
// calculateSIKAOddsTarget.test.ts assert agreement.
//
// Source: SIKA_Independence_Architecture_v1.0 §03.2 (Dynamic Odds
// Target Formula) — fills in the three-question narrative:
//   Q1 — What is the gap?            (drives base required return)
//   Q2 — What is the risk budget?    (banked → more aggression OK)
//   Q3 — What is the timeline?       (weeks ≤ 2 AND progress < 50% → urgent)
//
// Doctrine:
//   N02 — Never fix target at goal creation. This helper recomputes
//         each call from current goal state.
//   N08 — Urgent mode flagged transparently. is_urgent surfaces in
//         the rationale so the UI/agent must show it to the user.

export type SikaTier = "2x" | "3x" | "4x" | "5x";

export interface SikaGoalState {
  target_amount: number;
  current_amount: number;
  banked_amount: number;
  weeks_remaining: number;
  /** Optional — platform or user historical win rate. Currently
   *  reserved for future calibration; helper does not weight by it
   *  in SIKA-1 (kept in signature for forward compatibility). */
  user_win_rate?: number;
}

export interface SikaOddsTargetRationale {
  gap: number;
  base_required_return: number;
  base_target: number;
  risk_budget_multiplier: number;
  timeline_multiplier: number;
  is_urgent: boolean;
  /** Which of the three adjustments fired meaningfully (>1.0). */
  adjustments_fired: ReadonlyArray<"gap" | "risk_budget" | "timeline">;
  /** N08 — transparency phrase used by the agent when is_urgent. */
  urgent_note: string | null;
}

export interface SikaOddsTargetResult {
  dynamic_target: number;
  mapped_tier: SikaTier;
  is_urgent: boolean;
  rationale: SikaOddsTargetRationale;
}

const MIN_TARGET = 1.5;
const MAX_TARGET = 5.0;

/** Tier band boundaries. Match the ROLLOVER layer bands so
 *  rollover_candidates.tier lookups line up cleanly. */
export function mapTargetToTier(target: number): SikaTier {
  const clamped = Math.max(MIN_TARGET, Math.min(MAX_TARGET, target));
  if (clamped < 2.2) return "2x";
  if (clamped < 3.5) return "3x";
  if (clamped < 4.5) return "4x";
  return "5x";
}

export function buildRationale(
  goal: SikaGoalState,
  base_target: number,
  risk_budget_multiplier: number,
  timeline_multiplier: number,
  is_urgent: boolean,
): SikaOddsTargetRationale {
  const gap = goal.target_amount - goal.current_amount;
  const weeks = Math.max(1, goal.weeks_remaining);
  const base_required_return = gap / weeks;

  const adjustments_fired: Array<"gap" | "risk_budget" | "timeline"> = [];
  if (gap > 0) adjustments_fired.push("gap");
  if (risk_budget_multiplier > 1.01) adjustments_fired.push("risk_budget");
  if (timeline_multiplier > 1.01) adjustments_fired.push("timeline");

  return {
    gap,
    base_required_return,
    base_target: Number(base_target.toFixed(2)),
    risk_budget_multiplier: Number(risk_budget_multiplier.toFixed(3)),
    timeline_multiplier: Number(timeline_multiplier.toFixed(3)),
    is_urgent,
    adjustments_fired,
    urgent_note: is_urgent
      ? "This week's target is higher than usual. Goal is behind pace with only " +
        String(goal.weeks_remaining) +
        " week(s) remaining — increased risk. You decide."
      : null,
  };
}

export function calculateSIKAOddsTarget(goal: SikaGoalState): SikaOddsTargetResult {
  const target_amount = Math.max(0, goal.target_amount);
  const current_amount = Math.max(0, goal.current_amount);
  const banked_amount = Math.max(0, goal.banked_amount);
  const weeks_remaining = Math.max(1, goal.weeks_remaining);

  // Q1 — Gap → base required return per week.
  const gap = Math.max(0, target_amount - current_amount);
  const base_required_return = gap / weeks_remaining;
  // Base target = (required return / current_amount) + 1.
  // current_amount=0 is treated as a small denom so the formula
  // returns a sensible upper-band value rather than +Infinity.
  const denom = Math.max(1, current_amount);
  const raw_base = base_required_return / denom + 1;
  const base_target = Math.max(MIN_TARGET, Math.min(MAX_TARGET, raw_base));

  // Q2 — Risk budget: banked / target ratio. The more banked, the more
  // we can afford aggression (up to +30% multiplier).
  const risk_ratio = target_amount > 0 ? banked_amount / target_amount : 0;
  const risk_budget_multiplier = 1 + Math.min(0.3, Math.max(0, risk_ratio) * 0.3);

  // Q3 — Timeline pressure: weeks ≤ 2 AND progress < 50% → urgent.
  const progress_ratio = target_amount > 0 ? current_amount / target_amount : 0;
  const is_urgent = weeks_remaining <= 2 && progress_ratio < 0.5;
  const timeline_multiplier = is_urgent ? 1.5 : 1.0;

  const raw_dynamic = base_target * risk_budget_multiplier * timeline_multiplier;
  const dynamic_target = Number(
    Math.max(MIN_TARGET, Math.min(MAX_TARGET, raw_dynamic)).toFixed(2),
  );

  const rationale = buildRationale(
    goal,
    base_target,
    risk_budget_multiplier,
    timeline_multiplier,
    is_urgent,
  );

  return {
    dynamic_target,
    mapped_tier: mapTargetToTier(dynamic_target),
    is_urgent,
    rationale,
  };
}
